import React from 'react';

/**
 * Inline 1.5px-stroke line icons for the 28 schema types we generate, plus
 * a small set of facts icons used in the page-facts grid.
 *
 * All icons render at currentColor so they tint with surrounding text. Pass
 * size to scale (default 18). The visual language matches the editorial
 * paper/ink aesthetic — line work only, no fills.
 */

interface IconProps {
  size?: number;
  strokeWidth?: number;
  className?: string;
  style?: React.CSSProperties;
  'aria-hidden'?: boolean;
}

const base = (props: IconProps) => ({
  width: props.size ?? 18,
  height: props.size ?? 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: props.strokeWidth ?? 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  className: props.className,
  style: props.style,
  'aria-hidden': props['aria-hidden'] ?? true,
});

// ---------------------------------------------------------------------------
// Type → icon map
// ---------------------------------------------------------------------------

export function schemaTypeIcon(typeName: string, props: IconProps = {}): React.ReactElement {
  const Icon = TYPE_ICONS[typeName] || GenericDocIcon;
  return <Icon {...props} />;
}

const GenericDocIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);

const ArticleIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <path d="M8 13h8M8 17h8M8 9h2" />
  </svg>
);

const NewsIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <rect x="3" y="5" width="18" height="14" rx="1" />
    <path d="M7 9h6M7 13h10M7 17h10" />
    <rect x="14" y="9" width="3" height="2" />
  </svg>
);

const BlogIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M5 5h10l4 4v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z" />
    <path d="M15 5v4h4" />
    <path d="M7 13h7M7 17h5" />
  </svg>
);

const BookIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3z" />
    <path d="M5 17h14" />
  </svg>
);

const MovieIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <rect x="3" y="5" width="18" height="14" rx="1" />
    <path d="M7 5v14M17 5v14M3 9h4M3 14h4M17 9h4M17 14h4" />
  </svg>
);

const TVIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <rect x="3" y="6" width="18" height="12" rx="1" />
    <path d="M9 21h6M12 18v3" />
  </svg>
);

const MusicIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M9 17V5l11-2v12" />
    <circle cx="6" cy="17" r="3" />
    <circle cx="17" cy="15" r="3" />
  </svg>
);

const RecipeIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M4 11h16l-1 9H5z" />
    <path d="M9 7c0-2 1-3 3-3s3 1 3 3" />
    <path d="M7 11V8M17 11V8" />
  </svg>
);

const AudioIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M11 5L6 9H3v6h3l5 4z" />
    <path d="M16 9a4 4 0 0 1 0 6M19 6a8 8 0 0 1 0 12" />
  </svg>
);

const ImageIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <rect x="3" y="4" width="18" height="16" rx="1" />
    <circle cx="9" cy="10" r="2" />
    <path d="M21 16l-5-5-9 9" />
  </svg>
);

const VideoIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <rect x="3" y="5" width="18" height="14" rx="1" />
    <path d="M11 9l5 3-5 3z" />
  </svg>
);

const EventIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <rect x="3" y="5" width="18" height="16" rx="1" />
    <path d="M3 9h18M8 3v4M16 3v4" />
  </svg>
);

const OrganizationIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M3 21V8l9-5 9 5v13" />
    <path d="M9 21v-7h6v7M3 21h18" />
  </svg>
);

const PersonIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
  </svg>
);

const PlaceIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M12 21s7-7 7-12a7 7 0 0 0-14 0c0 5 7 12 7 12z" />
    <circle cx="12" cy="9" r="2.5" />
  </svg>
);

const LocalBusinessIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M3 9l1-4h16l1 4" />
    <path d="M3 9v11h18V9" />
    <path d="M9 20v-6h6v6" />
  </svg>
);

const RestaurantIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M6 3v8a2 2 0 0 0 2 2v8M10 3v8a2 2 0 0 1-2 2" />
    <path d="M16 3l-2 6c0 2 1 3 2 3v9" />
  </svg>
);

const ProductIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M12 3l9 4v10l-9 4-9-4V7z" />
    <path d="M3 7l9 4 9-4M12 11v10" />
  </svg>
);

const OfferIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M7 3h7l7 7-10 10-7-7z" />
    <circle cx="10" cy="10" r="1.5" />
  </svg>
);

const ReviewIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M12 3l2.5 5.5L20 9l-4 4 1 6-5-3-5 3 1-6-4-4 5.5-.5z" />
  </svg>
);

const RatingIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M5 9l1.5 3.5L10 13l-2.5 2.5.5 3.5L5 17l-3 2 .5-3.5L0 13l3.5-.5z" transform="translate(2 0)" />
    <path d="M11.5 9L13 12.5 16.5 13l-2.5 2.5.5 3.5-3-2-3 2 .5-3.5L6.5 13l3.5-.5z" transform="translate(2 0)" />
  </svg>
);

const FAQIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M12 17v.01" />
    <path d="M9 9a3 3 0 1 1 4.5 2.6c-.9.5-1.5 1.4-1.5 2.4" />
    <circle cx="12" cy="12" r="9" />
  </svg>
);

const HowToIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M4 6h2M9 6h11M4 12h2M9 12h11M4 18h2M9 18h11" />
  </svg>
);

const JobIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <rect x="3" y="7" width="18" height="13" rx="1" />
    <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 13h18" />
  </svg>
);

const CourseIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M2 9l10-5 10 5-10 5z" />
    <path d="M6 11v5c0 2 3 3 6 3s6-1 6-3v-5" />
  </svg>
);

const BreadcrumbIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M4 12h4l3-4M11 16l3-4h4M18 16l3-4" />
  </svg>
);

const TYPE_ICONS: Record<string, React.FC<IconProps>> = {
  CreativeWork: GenericDocIcon,
  Article: ArticleIcon,
  NewsArticle: NewsIcon,
  BlogPosting: BlogIcon,
  Book: BookIcon,
  Movie: MovieIcon,
  TVSeries: TVIcon,
  MusicRecording: MusicIcon,
  Recipe: RecipeIcon,
  AudioObject: AudioIcon,
  ImageObject: ImageIcon,
  VideoObject: VideoIcon,
  Event: EventIcon,
  Organization: OrganizationIcon,
  Person: PersonIcon,
  Place: PlaceIcon,
  LocalBusiness: LocalBusinessIcon,
  Restaurant: RestaurantIcon,
  Product: ProductIcon,
  Offer: OfferIcon,
  AggregateOffer: OfferIcon,
  Review: ReviewIcon,
  AggregateRating: RatingIcon,
  FAQPage: FAQIcon,
  HowTo: HowToIcon,
  JobPosting: JobIcon,
  Course: CourseIcon,
  BreadcrumbList: BreadcrumbIcon,
};

// ---------------------------------------------------------------------------
// Facts icons (for the page-facts grid)
// ---------------------------------------------------------------------------

export const FactIcons = {
  Heading: (p: IconProps = {}) => (
    <svg {...base(p)}>
      <path d="M5 4v16M19 4v16M5 12h14" />
    </svg>
  ),
  Image: ImageIcon,
  Video: VideoIcon,
  Audio: AudioIcon,
  Address: PlaceIcon,
  Phone: (p: IconProps = {}) => (
    <svg {...base(p)}>
      <path d="M5 4h4l2 5-3 2c1 3 3 5 6 6l2-3 5 2v4a2 2 0 0 1-2 2C9 22 2 15 2 6a2 2 0 0 1 2-2z" />
    </svg>
  ),
  Email: (p: IconProps = {}) => (
    <svg {...base(p)}>
      <rect x="3" y="5" width="18" height="14" rx="1" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  ),
  Price: OfferIcon,
  Date: (p: IconProps = {}) => (
    <svg {...base(p)}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  Breadcrumb: BreadcrumbIcon,
  Schema: (p: IconProps = {}) => (
    <svg {...base(p)}>
      <path d="M4 6h16M4 12h16M4 18h10" />
    </svg>
  ),
  Code: (p: IconProps = {}) => (
    <svg {...base(p)}>
      <path d="M8 8l-4 4 4 4M16 8l4 4-4 4M14 4l-4 16" />
    </svg>
  ),
};
